import { useState, useEffect, useMemo } from "react";
import { Clock, Search, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLES = {
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  open: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  partial: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  overdue: "bg-red-500/15 text-red-400 border-red-500/20",
};

export default function InvoicesTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${API}/quickbooks/invoices`, { headers });
        if (!res.ok) return;
        setData(await res.json());
      } catch {
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!data?.invoices) return [];
    return data.invoices.filter(inv => {
      if (statusFilter !== "all" && inv.status !== statusFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          inv.doc_number?.toLowerCase().includes(term) ||
          inv.customer_name?.toLowerCase().includes(term)
        );
      }
      return true;
    });
  }, [data, searchTerm, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <Clock className="w-10 h-10 text-white/20 mb-4" />
        <p className="text-white/40 text-sm">No data yet. Run a sync first.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoices-tab">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input
            type="text"
            placeholder="Search by invoice # or client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="invoices-search"
            className="w-full pl-9 pr-4 py-2 bg-white/[0.03] border border-white/[0.08] rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-white/20"
          />
        </div>
        <div className="flex gap-1.5">
          {["all", "open", "partial", "overdue", "paid"].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all capitalize ${
                statusFilter === s
                  ? "bg-white/10 text-white border border-white/20"
                  : "text-white/40 border border-white/[0.06] hover:bg-white/[0.04]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <span className="text-xs text-white/30 ml-auto">{filtered.length} invoice(s)</span>
      </div>

      {/* Invoice Table */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead>
              <tr className="text-white/30 text-[11px] uppercase tracking-wider bg-white/[0.02]">
                <th className="text-left px-4 py-3 font-medium">Invoice #</th>
                <th className="text-left px-4 py-3 font-medium">Client</th>
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-left px-4 py-3 font-medium">Due Date</th>
                <th className="text-right px-4 py-3 font-medium">Total</th>
                <th className="text-right px-4 py-3 font-medium">Balance</th>
                <th className="text-center px-4 py-3 font-medium">Status</th>
                <th className="text-center px-4 py-3 font-medium">Email</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-white/30 text-sm">
                    No invoices found
                  </td>
                </tr>
              ) : (
                filtered.map((inv, i) => (
                  <tr key={i} className="hover:bg-white/[0.02] transition-colors group">
                    <td className="px-4 py-3 text-white/80 font-medium">{inv.doc_number}</td>
                    <td className="px-4 py-3 text-white/60">{inv.customer_name}</td>
                    <td className="px-4 py-3 text-white/40 text-xs">{inv.txn_date}</td>
                    <td className="px-4 py-3 text-white/40 text-xs">{inv.due_date}</td>
                    <td className="px-4 py-3 text-right text-white/60">
                      ${inv.total_amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-right text-white/80 font-medium">
                      {inv.balance > 0
                        ? `$${inv.balance.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                        : <span className="text-emerald-400/60">Paid</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border capitalize ${STATUS_STYLES[inv.status] || ""}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-[10px] ${inv.email_status === "EmailSent" ? "text-emerald-400/60" : "text-white/20"}`}>
                        {inv.email_status === "EmailSent" ? "Sent" : inv.email_status === "NeedToSend" ? "Pending" : "-"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
